import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/app.materials';
import { HttpResponse } from '@angular/common/http';
import { ReportService } from 'src/app/shared/services/report.service';
import { ViewEncapsulation } from '@angular/core';

export interface PdfExportDialogData {
  portfolioId: number;
  portfolioName?: string;
  efficientFrontierData?: any[];
  targetPortfolio?: string;
}

export interface ReportSection {
  label: string;
  component: string;
  selected: boolean;
  group: string;
}

export interface SectionGroup {
  title: string;
  sections: ReportSection[];
}

const ALL_SECTIONS: ReportSection[] = [
  { label: 'Portfolio Description', component: 'portfolio-description', selected: true, group: 'Backtest' },
  { label: 'Portfolio Overview', component: 'portfolio-overview', selected: true, group: 'Backtest' },
  { label: 'Portfolio Weights Performance', component: 'portfolio-weights-performance', selected: true, group: 'Backtest' },
  { label: 'Cumulative Return Chart', component: 'cumulative-return-chart', selected: true, group: 'Backtest' },
  { label: 'Annual Performance Chart', component: 'annual-performance-chart', selected: true, group: 'Backtest' },
  { label: 'Portfolio Constituents Table', component: 'portfolio-constituents-table', selected: true, group: 'Backtest' },
  { label: 'Summary Risk Contribution', component: 'summary-risk-contribution', selected: true, group: 'Backtest' },
  { label: 'Summary Marginal Risk', component: 'summary-marginal-risk', selected: true, group: 'Backtest' },
  { label: 'Covariance Correlation Table', component: 'covariance-correlation-table', selected: true, group: 'Optimization' },
  { label: 'Implied Views', component: 'implied-views', selected: true, group: 'Optimization' },
  { label: 'My Views', component: 'my-views', selected: true, group: 'Optimization' },
  { label: 'Combined Returns', component: 'combined-returns', selected: true, group: 'Optimization' },
  { label: 'Constraints', component: 'constraints', selected: true, group: 'Optimization' },
  { label: 'Efficient Frontier', component: 'efficient-frontier', selected: true, group: 'Optimization' },
  { label: 'Transition Map', component: 'transition-map', selected: true, group: 'Optimization' },
  { label: 'Optimization Summary', component: 'optimization-summary', selected: true, group: 'Optimization' },
  { label: 'Risk Expected Risk', component: 'risk-expected-risk', selected: true, group: 'Risk Analysis' },
  { label: 'Risk Contribution Allocation', component: 'risk-contribution-allocation', selected: true, group: 'Risk Analysis' },
  { label: 'Covariance Matrix Table Data', component: 'covariance-matrix-table-data', selected: true, group: 'Risk Analysis' },
  { label: 'Correlation Matrix Table Data', component: 'correlation-matrix-table-data', selected: true, group: 'Risk Analysis' },
  { label: 'Hot Spots', component: 'hot-spots', selected: true, group: 'Risk Analysis' },
  { label: 'Risk Drawdown', component: 'risk-drawdown', selected: true, group: 'Risk Analysis' },
  { label: 'Risk Volatility', component: 'risk-volatility', selected: true, group: 'Risk Analysis' },
];

@Component({
  selector: 'app-pdf-export-dialog',
  templateUrl: './pdf-export-dialog.component.html',
  styleUrls: ['./pdf-export-dialog.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, FormsModule, MaterialModule],
})
export class PdfExportDialogComponent {
  title = 'Portfolio Report';
  subtitle = '';
  sections: ReportSection[] = ALL_SECTIONS.map(s => ({ ...s }));
  orderedSections: ReportSection[] = [...this.sections];
  exporting = false;
  error: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PdfExportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PdfExportDialogData,
    private reportService: ReportService
  ) {
    if (data.portfolioName) {
      this.title = data.portfolioName + ' Report';
    }
  }

  onSectionToggle(section: ReportSection, checked: boolean): void {
    section.selected = checked;
    if (checked) {
      this.orderedSections = [...this.orderedSections, section];
    } else {
      this.orderedSections = this.orderedSections.filter(s => s.component !== section.component);
    }
  }

  onDrop(event: CdkDragDrop<ReportSection[]>): void {
    moveItemInArray(this.orderedSections, event.previousIndex, event.currentIndex);
  }

  onExport(): void {
    this.error = null;
    this.exporting = true;

    const structure = this.orderedSections.map((s, i) => ({
      component: s.component,
      order: i + 1,
    }));

    const payload = {
      portfolio_id: this.data.portfolioId,
      meta: {
        title: this.title,
        subtitle: this.subtitle,
      },
      options: {
        target_portfolio: this.data.targetPortfolio || 'efficient_portfolio_max_sharpe_ratio',
        efficient_frontier_data: this.data.efficientFrontierData || []
      },
      structure: structure,
    };

    this.reportService.generateReport(payload).subscribe({
      next: (response: HttpResponse<Blob>) => {
        this.exporting = false;
        const blob = response.body;
        const filename = this.extractFilename(response) || `${this.title || 'report'}.pdf`;
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        // Delay revoke so the browser has time to initiate the download
        setTimeout(() => window.URL.revokeObjectURL(url), 200);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.exporting = false;
        const blobError: Blob = err?.error;
        if (blobError instanceof Blob) {
          blobError.text().then(text => {
            try {
              const parsed = JSON.parse(text);
              this.error = parsed?.message || text || 'Failed to generate report.';
            } catch {
              this.error = text || 'Failed to generate report.';
            }
          });
        } else {
          this.error = err?.message || err || 'Failed to generate report. Please try again.';
        }
      },
    });
  }

  get sectionGroups(): SectionGroup[] {
    const groupMap = new Map<string, ReportSection[]>();
    for (const section of this.sections) {
      if (!groupMap.has(section.group)) {
        groupMap.set(section.group, []);
      }
      groupMap.get(section.group).push(section);
    }
    return Array.from(groupMap.entries()).map(([title, sections]) => ({ title, sections }));
  }

  get allSelected(): boolean {
    return this.sections.every(s => s.selected);
  }

  get someSelected(): boolean {
    return this.sections.some(s => s.selected) && !this.allSelected;
  }

  trackByGroup(_: number, group: SectionGroup): string {
    return group.title;
  }

  trackBySection(_: number, section: ReportSection): string {
    return section.component;
  }

  onToggleAll(checked: boolean): void {
    this.sections.forEach(s => (s.selected = checked));
    this.orderedSections = checked ? [...this.sections] : [];
  }

  private extractFilename(response: HttpResponse<Blob>): string | null {
    const disposition = response.headers.get('Content-Disposition');
    if (!disposition) return null;
    const match = disposition.match(/filename="?([^";\n]+)"?/i);
    return match ? match[1].trim() : null;
  }
}
